import { Link } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";
import { trustStats } from "@/mocks/testimonials";

const trustPoints = [
  { icon: "ri-shield-check-line", title: "Fully qualified", body: "UK certified electrician, Part P registered." },
  { icon: "ri-map-pin-2-line", title: "Local and independent", body: "Just one skilled electrician, working nearby." },
  { icon: "ri-price-tag-3-line", title: "Clear pricing", body: "You'll know the likely cost before I start." },
  { icon: "ri-brush-line", title: "Tidy workmanship", body: "Places left neater than I found them." },
  { icon: "ri-chat-smile-2-line", title: "Friendly communication", body: "No jargon, just straight answers." },
  { icon: "ri-home-4-line", title: "Homes & small businesses", body: "Real residential and small commercial experience." },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About"
        title={
          <>
            Independent, local,<br />
            <em className="not-italic text-accent-400">and here to help.</em>
          </>
        }
        intro="I'm an independent electrician serving homes and small businesses across Islington and nearby North London."
        image="https://readdy.ai/api/search-image?query=Confident%20UK%20electrician%20in%20navy%20workwear%20holding%20a%20drill%20standing%20outside%20a%20London%20Islington%20terraced%20brick%20house%20with%20a%20black%20front%20door%2C%20warm%20golden%20late%20afternoon%20light%2C%20candid%20portrait%2C%20realistic%20editorial%20photography&width=1400&height=900&seq=about-hero-01&orientation=landscape"
        imageAlt="Portrait of the independent local electrician outside an Islington home"
        primaryLabel="Book Appointment"
        secondaryLabel="See services"
        secondaryTo="/services"
      />

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-2 h-2 rounded-full bg-foreground-950"></span>
              <span className="text-[11px] uppercase tracking-[0.18em] text-foreground-700">
                In my own words
              </span>
            </div>
            <blockquote className="relative pl-3 sm:pl-0">
              <span className="absolute -top-4 sm:-top-6 -left-1 sm:-left-2 text-6xl sm:text-7xl md:text-8xl font-heading text-accent-500 leading-none select-none">
                &ldquo;
              </span>
              <p className="text-2xl sm:text-3xl md:text-[42px] font-heading font-medium tracking-tight leading-[1.15] text-foreground-950">
                I focus on safe, tidy and straightforward electrical work, with
                clear communication from first message to final check. I turn up
                when agreed, explain the work clearly, and leave the place tidy.
              </p>
            </blockquote>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div>
                <p className="text-base font-semibold text-foreground-950">
                  James Wallace
                </p>
                <p className="text-xs text-foreground-600">
                  Independent electrician · Islington N1
                </p>
              </div>
              <span className="w-px h-8 bg-background-300"></span>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-100 border border-background-200 text-xs text-foreground-800">
                <i className="ri-star-fill text-emerald-500"></i>
                4.9 · Verified locally
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="rounded-2xl overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20candid%20portrait%20of%20an%20independent%20UK%20electrician%20in%20navy%20workwear%20smiling%20inside%20a%20London%20flat%20hallway%20with%20natural%20window%20light%2C%20confident%20friendly%20expression%2C%20realistic%20editorial%20portrait%20photography%2C%20soft%20shadows&width=640&height=800&seq=about-portrait-01&orientation=portrait"
                alt="Portrait of James, the local electrician"
                loading="lazy" decoding="async"
                className="w-full aspect-[4/5] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-100">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-background-50 border border-background-200 text-[11px] uppercase tracking-[0.18em] text-foreground-700">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
              Why locals trust me
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-heading font-semibold tracking-tight text-foreground-950 leading-[1.05] max-w-2xl">
              Small details<br />
              <em className="not-italic text-accent-600">matter here.</em>
            </h2>

            <div className="mt-6 sm:mt-8 grid grid-cols-3 gap-3 sm:gap-4 border-t border-background-300 pt-6 sm:pt-8">
              {trustStats.map((s, i) => (
                <div
                  key={s.label}
                  className={`pr-3 ${i < 2 ? "border-r border-background-300" : ""}`}
                >
                  <div className="text-2xl sm:text-3xl md:text-5xl font-heading font-semibold text-accent-600 tracking-tight">
                    {s.value}
                  </div>
                  <div className="mt-2 text-xs md:text-sm text-foreground-600 leading-snug">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
            {trustPoints.map((p) => (
              <div
                key={p.title}
                className="p-5 rounded-xl bg-background-50 border border-background-200 hover:border-accent-500 transition-colors"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-accent-700">
                  <i className={`${p.icon} text-lg`}></i>
                </span>
                <p className="mt-4 text-sm font-semibold text-foreground-950">
                  {p.title}
                </p>
                <p className="mt-1 text-xs text-foreground-600 leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "On the tools",
              body: "Over 12 years on live electrical work — from tiny socket swaps to full rewires. Islington and nearby North London is where I live and work.",
              image:
                "https://readdy.ai/api/search-image?query=Independent%20UK%20electrician%20wiring%20a%20new%20socket%20on%20a%20London%20flat%20wall%20with%20focused%20hands%20close%20up%2C%20warm%20natural%20light%2C%20realistic%20editorial%20photography%2C%20detailed%20texture&width=720&height=560&seq=about-work-01&orientation=landscape",
            },
            {
              title: "Off the clock",
              body: "You'll probably see me on Upper Street with a coffee or fixing a neighbour's dodgy light. Local means local.",
              image:
                "https://readdy.ai/api/search-image?query=Casual%20UK%20electrician%20in%20a%20denim%20jacket%20walking%20past%20a%20London%20independent%20coffee%20shop%20on%20a%20quiet%20Islington%20street%2C%20warm%20late%20afternoon%20light%2C%20realistic%20candid%20editorial%20photography&width=720&height=560&seq=about-life-02&orientation=landscape",
            },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl overflow-hidden bg-background-100 border border-background-200">
              <div className="relative aspect-[16/10]">
                <img src={b.image} alt={b.title} loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-5 sm:p-6 md:p-8">
                <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-semibold text-foreground-950">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  {b.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection
        eyebrow="Say hi"
        title={
          <>
            Prefer a quick chat<br />
            <em className="not-italic text-accent-600">before booking?</em>
          </>
        }
        subtitle="Send a message and I'll get back to you as soon as I can."
        primaryLabel="Book Appointment"
        secondaryLabel="Send a message"
        secondaryTo="/contact"
      />
    </>
  );
}